import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Bot, 
  CreditCard, 
  MessageSquare, 
  Settings, 
  ChevronDown,
  ChevronUp,
  BarChart2,
  Search,
  User,
  Bell,
  MoreVertical
} from 'lucide-react';
import { Button } from '../ui/Button';

// Simulated user data for admin dashboard
const users = [
  { id: '1', name: 'John Smith', email: 'john@example.com', plan: 'Pro', status: 'active', bots: 3, lastLogin: '2 hours ago' },
  { id: '2', name: 'Sarah Johnson', email: 'sarah@example.com', plan: 'Ultimate', status: 'active', bots: 12, lastLogin: '5 minutes ago' },
  { id: '3', name: 'Michael Brown', email: 'michael@example.com', plan: 'Free', status: 'inactive', bots: 1, lastLogin: '3 days ago' },
  { id: '4', name: 'Emily Williams', email: 'emily@example.com', plan: 'Pro', status: 'active', bots: 5, lastLogin: '1 hour ago' },
  { id: '5', name: 'David Miller', email: 'david@example.com', plan: 'Free', status: 'pending', bots: 0, lastLogin: 'Never' },
];

export const AdminDashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      {/* Dashboard layout */}
      <div className="flex">
        {/* Sidebar */}
        <div className="hidden md:flex w-64 flex-col fixed inset-y-0 pt-16 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          <div className="flex flex-col flex-grow p-4 overflow-y-auto">
            <div className="flex items-center px-4 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-center h-8 w-8 bg-red-100 dark:bg-red-900/20 text-red-600 rounded-md">
                <Settings className="h-5 w-5" />
              </div>
              <span className="ml-3 text-lg font-bold text-gray-900 dark:text-white">Admin Panel</span>
            </div>
            
            <div className="space-y-4 mt-8">
              <button
                onClick={() => setActiveTab('users')}
                className={`flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 rounded-lg ${
                  activeTab === 'users' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Users className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Users</span>
              </button>
              <button
                onClick={() => setActiveTab('bots')}
                className={`flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 rounded-lg ${
                  activeTab === 'bots' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Bot className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Active Bots</span>
              </button>
              <button
                onClick={() => setActiveTab('subscriptions')}
                className={`flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 rounded-lg ${
                  activeTab === 'subscriptions' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <CreditCard className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Subscriptions</span>
              </button>
              <button
                onClick={() => setActiveTab('messages')}
                className={`flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 rounded-lg ${
                  activeTab === 'messages' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <MessageSquare className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Messages</span>
                <span className="ml-auto bg-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                  5
                </span>
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 rounded-lg ${
                  activeTab === 'analytics' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <BarChart2 className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Analytics</span>
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 rounded-lg ${
                  activeTab === 'settings' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Settings className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                <span>Settings</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Nav */}
        <div className="md:hidden fixed inset-x-0 top-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
          <div className="flex overflow-x-auto px-4 py-3 space-x-4">
            <button
              onClick={() => setActiveTab('users')}
              className={`flex flex-col items-center py-1 px-2 ${
                activeTab === 'users' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <Users className="h-5 w-5" />
              <span className="text-xs mt-1">Users</span>
            </button>
            <button
              onClick={() => setActiveTab('bots')}
              className={`flex flex-col items-center py-1 px-2 ${
                activeTab === 'bots' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <Bot className="h-5 w-5" />
              <span className="text-xs mt-1">Bots</span>
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`flex flex-col items-center py-1 px-2 ${
                activeTab === 'subscriptions' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <CreditCard className="h-5 w-5" />
              <span className="text-xs mt-1">Subs</span>
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex flex-col items-center py-1 px-2 ${
                activeTab === 'messages' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <MessageSquare className="h-5 w-5" />
              <span className="text-xs mt-1">Messages</span>
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`flex flex-col items-center py-1 px-2 ${
                activeTab === 'analytics' ? 'text-blue-500 border-b-2 border-blue-500' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <BarChart2 className="h-5 w-5" />
              <span className="text-xs mt-1">Analytics</span>
            </button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="md:ml-64 flex-1">
          <main className="p-4 md:p-8 mt-12 md:mt-0">
            {activeTab === 'users' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
                    <p className="text-gray-600 dark:text-gray-400">View and manage all registered users.</p>
                  </div>
                  
                  <div className="mt-4 md:mt-0 w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
                    <div className="relative w-full md:w-64">
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 pl-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                    
                    <Button 
                      variant="primary" 
                      icon={<User size={16} />}
                      iconPosition="left"
                    >
                      Add User
                    </Button>
                  </div>
                </div>
                
                {/* User Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">1,248</p>
                      </div>
                      <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                        <Users className="h-6 w-6 text-blue-500" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      <span className="text-green-500">↑ 12%</span> from last month
                    </p>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">New Sign-ups</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">48</p>
                      </div>
                      <div className="h-12 w-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                        <User className="h-6 w-6 text-green-500" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      <span className="text-green-500">↑ 8%</span> from yesterday
                    </p>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pro Plan Users</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">762</p>
                      </div>
                      <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                        <CreditCard className="h-6 w-6 text-purple-500" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      <span className="text-green-500">↑ 15%</span> from last month
                    </p>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Bots</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">3,571</p>
                      </div>
                      <div className="h-12 w-12 bg-pink-100 dark:bg-pink-900/20 rounded-lg flex items-center justify-center">
                        <Bot className="h-6 w-6 text-pink-500" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      <span className="text-green-500">↑ 22%</span> from last week
                    </p>
                  </div>
                </div>
                
                {/* Users Table */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Users</h2>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Status:</span>
                      <select className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-1 px-2">
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Plan</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bots</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Login</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredUsers.map((user) => (
                          <tr key={user.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                  <span className="font-medium text-gray-600 dark:text-gray-300">
                                    {user.name.charAt(0)}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                user.plan === 'Ultimate' 
                                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                  : user.plan === 'Pro'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {user.plan}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                user.status === 'active' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : user.status === 'inactive'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              }`}>
                                {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {user.bots}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {user.lastLogin}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end space-x-2">
                                <button className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300">
                                  Edit
                                </button>
                                <button className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                  <Bell size={16} />
                                </button>
                                <button className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                  <MoreVertical size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Showing {filteredUsers.length} of {users.length} users
                    </div>
                    <div className="flex space-x-1">
                      <button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                        Previous
                      </button>
                      <button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-blue-500 text-white text-sm hover:bg-blue-600">
                        1
                      </button>
                      <button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                        2
                      </button>
                      <button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                        3
                      </button>
                      <button className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};